import { useCallback, useEffect, useRef, useState } from 'react'
import { SignInManager } from '../SignInManager'
import { customAmplitude } from '@/utils/customAmplitude'
import { captureException } from '@sentry/react'
import {
  buildSignUpClickProperties,
  buildSignupAnalyticsContext,
  type SignupAnalyticsContext,
} from '@/utils/signupPremiumAnalytics'

export type SocialProvider = 'google' | 'facebook' | 'apple'

// How long the buttons stay busy before we show a retry message. Google and
// Apple sign-ins of new users routinely run past 30s (password, 2FA and the
// first-time consent screen), so keep this generous. The provider flow keeps
// running after the timeout and a late success still completes the login.
export const SOCIAL_LOGIN_TIMEOUT_MS = 90000

// Keeps the original message (and error_code 'error') so analytics charts that
// filter on "Social login timed out for <provider>" keep working.
class SocialLoginTimeoutError extends Error {
  constructor(social: SocialProvider) {
    super(`Social login timed out for ${social}`)
    this.name = 'SocialLoginTimeoutError'
  }
}

const getErrorDetails = (error: unknown): { errorCode: string; errorMessage: string } => {
  if (error instanceof Error) {
    const maybeCode = error as Error & { code?: string }
    return {
      errorCode: maybeCode.code ?? 'error',
      errorMessage: error.message || 'Social login error',
    }
  }

  if (typeof error === 'string') {
    return { errorCode: 'error', errorMessage: error }
  }

  return { errorCode: 'unknown', errorMessage: 'Social login error' }
}

const withTimeout = <T,>(promise: Promise<T>, social: SocialProvider) =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new SocialLoginTimeoutError(social))
    }, SOCIAL_LOGIN_TIMEOUT_MS)

    promise
      .then((value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      })
  })

interface UseSocialLoginOptions {
  onSuccess?: (user: any) => void
  onError?: (error: Error, social: SocialProvider) => void
  getAnalyticsContext?: () => SignupAnalyticsContext
}

export function useSocialLogin(options: UseSocialLoginOptions = {}) {
  const [loadingSocial, setLoadingSocial] = useState<SocialProvider | null>(null)
  const [socialError, setSocialError] = useState<SocialProvider | null>(null)
  const isMountedRef = useRef(true)
  const attemptRef = useRef(0)
  const startGuard = useRef(false)

  // Invalidate any pending social attempt so a late provider result can no
  // longer complete the login: call when the user leaves the social flow
  // (e.g. opens email sign-in). The manager also cancels provider work and
  // token exchange before a late result can create a session.
  const cancelPendingSocialLogin = useCallback(() => {
    attemptRef.current += 1
    startGuard.current = false
    if (isMountedRef.current) setLoadingSocial(null)
    SignInManager.getInstance().cancelPendingSocialLogin()
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      cancelPendingSocialLogin()
    }
  }, [cancelPendingSocialLogin])

  const login = async (social: SocialProvider) => {
    if (startGuard.current) {
      return
    }

    const attempt = ++attemptRef.current
    startGuard.current = true
    setLoadingSocial(social)
    setSocialError(null)
    let user
    let providerLogin: Promise<any> | undefined

    try {
      const analyticsContext = options.getAnalyticsContext?.()
      const signUpClickPayload = buildSignUpClickProperties({
        context:
          analyticsContext ??
          buildSignupAnalyticsContext({
            source: null,
            type: 'Standard',
          }),
        destination: social,
      })

      customAmplitude('Sign up click', signUpClickPayload)

      if (social === 'facebook') {
        providerLogin = SignInManager.getInstance().loginWithFacebook()
      } else if (social === 'google') {
        providerLogin = SignInManager.getInstance().loginWithGoogle()
      } else if (social === 'apple') {
        providerLogin = SignInManager.getInstance().loginWithApple()
      }
      if (providerLogin) {
        user = await withTimeout(providerLogin, social)
      }
    } catch (error) {
      const timedOut = error instanceof SocialLoginTimeoutError
      if (timedOut && providerLogin) {
        // The provider window may still be open (password, 2FA, consent).
        // SignInManager finishes the session when the credential arrives, so
        // finish the UI flow too instead of leaving the user on an error.
        providerLogin
          .then((lateUser) => {
            if (!lateUser || !isMountedRef.current || attempt !== attemptRef.current) {
              return
            }
            setLoadingSocial(null)
            setSocialError(null)
            options.onSuccess?.(lateUser)
          })
          .catch(() => {
            // A late provider failure after the timeout was already reported.
          })
      }
      if (!isMountedRef.current || attempt !== attemptRef.current) return
      startGuard.current = false
      const { errorCode, errorMessage } = getErrorDetails(error)
      captureException(error, {
        level: timedOut ? 'warning' : 'fatal',
        extra: { customMessage: 'Social login error' },
        tags: { module: 'login' },
      })
      customAmplitude('Sign up error', {
        ...buildSignUpClickProperties({
          context:
            options.getAnalyticsContext?.() ??
            buildSignupAnalyticsContext({
              source: null,
              type: 'Standard',
            }),
          destination: social,
        }),
        error: 'Social login error',
        error_code: errorCode,
        error_message: errorMessage,
        'Auth Flow': 'SignIn',
      })
      if (!isMountedRef.current) {
        return
      }
      setLoadingSocial(null)
      setSocialError(social)
      options.onError?.(error as Error, social)
      return
    }

    // Post-login handling - outside try-catch to avoid misreporting errors
    if (!isMountedRef.current || attempt !== attemptRef.current) {
      return
    }
    startGuard.current = false
    setLoadingSocial(null)

    // A null/undefined user means the user canceled or aborted the provider
    // flow (e.g. closed the Facebook/Google/Apple popup) without authenticating.
    // Do not treat this as success - keep the sign-up screen visible instead of
    // letting the user into the app unauthenticated.
    if (!user) {
      return
    }

    options.onSuccess?.(user)
  }

  return { login, loadingSocial, socialError, cancelPendingSocialLogin }
}
